import {Box, InputBase} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type {TFunction} from "i18next";
import React, {Ref, useId} from "react";
import {signingControls} from "./signingColors";
import {SigningFormat} from "./format.util";
import {SnapshotTip} from "./digitalSigning.types";
import {TIP_PILL_HEIGHT} from "./TipPill";
import {customTipCents, customTipProblem, finishAmountInput, isUnfinishedAmount, tipPercent} from "./tip.util";

interface Props {
    tip: SnapshotTip;
    value: string;
    onChange?: (value: string) => void;
    format: SigningFormat;
    t: TFunction;
    disabled?: boolean;
    readOnlyCents?: number;
    inputRef?: Ref<HTMLInputElement>;
}

const FIELD_SX = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    height: {xs: 52, sm: TIP_PILL_HEIGHT.sm},
    width: '100%',
    maxWidth: 280,
    px: 2,
    border: '1px solid',
    borderRadius: 1,
} as const;

function TipCustomAmount({tip, value, onChange, format, t, disabled, readOnlyCents, inputRef}: Props) {
    const theme = useTheme();
    const controls = signingControls(theme);
    const helperId = useId();
    const readOnly = readOnlyCents !== undefined;

    const unfinished = !readOnly && isUnfinishedAmount(value);
    const cents: number | null = readOnlyCents ?? customTipCents(value);
    const typed = readOnly || (value.trim() !== '' && !unfinished);
    const problem = typed && !readOnly ? customTipProblem(cents) : undefined;
    const percent = typed && cents !== null && !problem ? tipPercent(cents, tip.baseCents) : undefined;
    const ring = problem ? theme.palette.error.main : controls.focus;

    const helper = problem === 'INVALID'
        ? t('Please enter a valid amount.')
        : problem === 'NOT_POSITIVE'
            ? t('Please enter an amount above zero.')
            : percent !== undefined
                ? t('{{percent}} of {{amount}}', {percent: format.percent(percent), amount: format.money(tip.baseCents)})
                : '';

    const symbol = !!format.currencySymbol && (
        <Typography component={'span'} aria-hidden color={'text.secondary'} sx={{fontSize: 18}}>
            {format.currencySymbol}
        </Typography>
    );

    return (
        <Box sx={{mt: 2}}>
            {readOnly ? (
                <Box sx={{...FIELD_SX, borderColor: controls.border}}>
                    {symbol}
                    <Typography component={'span'} sx={{fontSize: 20, fontVariantNumeric: 'tabular-nums'}}>
                        {format.amount(readOnlyCents ?? 0)}
                    </Typography>
                </Box>
            ) : (
                <Box
                    component={'label'}
                    sx={{
                        ...FIELD_SX,
                        cursor: disabled ? 'default' : 'text',
                        borderColor: problem ? 'error.main' : controls.border,
                        transition: 'border-color 160ms, box-shadow 160ms',
                        '&:focus-within': {borderColor: ring, boxShadow: `0 0 0 1px ${ring}`},
                        ...(disabled && {opacity: 0.5}),
                    }}
                >
                    {symbol}
                    <InputBase
                        inputRef={inputRef}
                        value={value}
                        disabled={disabled}
                        onChange={event => onChange?.(event.target.value)}
                        onBlur={() => {
                            const finished = finishAmountInput(value);
                            if (finished !== value) onChange?.(finished);
                        }}
                        onKeyDown={event => {
                            if (event.key !== 'Enter') return;
                            event.preventDefault();
                            event.currentTarget.blur();
                        }}
                        placeholder={format.amountPlaceholder}
                        inputProps={{
                            inputMode: 'decimal',
                            autoComplete: 'off',
                            enterKeyHint: 'done',
                            maxLength: 10,
                            'aria-label': t('Tip amount'),
                            'aria-describedby': helperId,
                            'aria-invalid': !!problem,
                        }}
                        sx={{
                            flex: 1,
                            fontSize: 20,
                            fontVariantNumeric: 'tabular-nums',
                            color: 'text.primary',
                            '& input::placeholder': {color: 'text.secondary', opacity: 1},
                        }}
                    />
                </Box>
            )}
            <Typography
                id={helperId}
                variant={'body2'}
                aria-live={readOnly ? undefined : 'polite'}
                color={problem ? 'error.main' : 'text.secondary'}
                sx={{mt: 0.75, minHeight: '1.45em'}}
            >
                {helper}
            </Typography>
        </Box>
    );
}

export default TipCustomAmount;
